import { inject, observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import * as React from 'react';

import { ClickTracker } from 'bernie-analytics';
import { ILocalization, withLocalization } from 'bernie-l10n';
import { RouteStore } from 'bernie-core';

import { useLocalization } from '@shared-ui/localization-context';

import { PriceLockupStids, PriceSummary, PriceViewModel } from 'models';
import { Theme } from 'constants/theme';

import { UitkLink } from 'uitk-react-link';
import { UitkPriceLockup } from 'uitk-react-price-lockup';
import { UitkPrimaryButton } from 'uitk-react-button';
import { UitkText } from 'uitk-react-text';

import { DisclaimerDialog } from 'components/common/disclaimer-dialog';
import { DoubleLink } from 'components/common/media/double-link';
import { PriceBreakDown } from 'components/common/pricing/price-breakdown';
import { TargetableLink } from 'components/common/targetable-link';

export interface PriceLockupProps {
  price: PriceViewModel;
  routeStore?: RouteStore;
  uniqueId?: number;
  isPwaDialogNested?: boolean;
  supportingMessages?: string[];
  lodgingPriceBreakDown?: PriceSummary;
  infositeUrl?: string;
  stids?: PriceLockupStids;
  l10n: ILocalization;
  excludePriceBreakDownLink?: boolean;
}

const LinkTracked = ClickTracker(Link);
const ButtonTracked = ClickTracker(UitkPrimaryButton);
@withLocalization
@inject('routeStore')
@observer
export class PriceLockup extends React.Component<PriceLockupProps> {
  public onDismissCB = () => {
    if (this.props.routeStore) {
      this.props.routeStore.closeDialog();
    }
  };

  public render() {
    const {
      routeStore,
      price,
      uniqueId,
      isPwaDialogNested,
      supportingMessages,
      lodgingPriceBreakDown,
      infositeUrl,
      stids,
      l10n,
      excludePriceBreakDownLink,
    } = this.props;
    // Field names are confusing. price message is the message we display when price field is null
    // PriceMessages are list of messages that get stacked bellow the lead price

    const { options, priceMessage, priceMessages, pointsApplied } = price;

    // This is because UITK-pricelockup component does not support partners price dispaly just yet.
    // There is a plan to support partners in the future

    const lockupPrice = options?.[0]?.displayPrice?.formatted;
    const strikeThroughPrice = options?.[0]?.strikeOut?.formatted;
    const disclaimerValue = options?.[0]?.disclaimer?.value || price.strikeoutDisclaimer;
    const a11yPrice =
      strikeThroughPrice && lockupPrice
        ? l10n.formatText('hotels.infosite.rateInfoDialog.leadWithStrikeOutPresent.a11yLabel', strikeThroughPrice, lockupPrice)
        : l10n.formatText('hotels.infosite.rateInfoDialog.lead', lockupPrice);

    const a11yStrikeThroughDialogTrigger = l10n.formatText('hotels.infosite.rateInfo.moreDetails.linkText');

    const priceSubtextFirst = priceMessages && priceMessages.length > 0 ? priceMessages.map(message => message.value) : undefined;

    let priceSubtextBold = null;
    let priceSubtextStandard = null;

    if (supportingMessages && supportingMessages.length > 0) {
      priceSubtextBold = supportingMessages[0];
      if (supportingMessages.length > 1) {
        [, ...priceSubtextStandard] = supportingMessages;
      }
    }
    if (pointsApplied) {
      priceSubtextStandard.push(pointsApplied);
    }
    const dialogId = uniqueId ? `priceDisclaimerDialog-${uniqueId}` : 'priceDisclaimerDialog';
    const pwaDialog = isPwaDialogNested ? { pwaDialogNested: dialogId } : { pwaDialog: dialogId };
    const dialogType = isPwaDialogNested ? 'pwaDialogNested' : 'pwaDialog';

    const priceBreakDownWrapperProps = {
      lodgingPriceBreakDown,
      infositeUrl,
      layerId: uniqueId,
    };

    return (
      <>
        {priceMessage && <PriceDescription {...priceMessage} />}
        {!priceMessage && (
          <>
            <UitkPriceLockup
              lockupPrice={lockupPrice}
              priceSubtextFirst={priceSubtextFirst}
              priceSubtextStandard={priceSubtextStandard}
              strikeThroughPrice={strikeThroughPrice}
              a11yPrice={a11yPrice}
              stids={stids}
              a11yStrikeThroughDialogTrigger={a11yStrikeThroughDialogTrigger}
              strikeThroughTrigger={
                <LinkTracked
                  to={{
                    pathname: routeStore.pathname,
                    search: routeStore.mergeQueryString(routeStore.search, pwaDialog),
                  }}
                  linkName="Price Disclaimer Dialog"
                  rfrr=".Price.DisclaimerDialog"
                />
              }
              priceSubtextBold={priceSubtextBold}
            />
            {dialogId && disclaimerValue && (
              <DisclaimerDialog
                dialogId={dialogId}
                dialogType={dialogType}
                disclaimerText={disclaimerValue}
                handleClose={this.onDismissCB}
                innerHTML={true}
                layerId={`layer-id-${dialogId}`}
                disclaimerTextId="price-disclaimer-text"
              />
            )}

            {!excludePriceBreakDownLink && lodgingPriceBreakDown && <PriceBreakDownWrapper {...priceBreakDownWrapperProps} />}
          </>
        )}
      </>
    );
  }
}

const PriceBreakDownWrapper = ({ lodgingPriceBreakDown, infositeUrl, layerId }) => {
  const l10n = useLocalization();
  const extendedChildActions = (options: any): React.ReactNode => (
    <TargetableLink>
      <UitkLink inline={false} className="all-x-padding-four all-t-margin-two">
        <DoubleLink to={infositeUrl}>
          <ButtonTracked
            isFullWidth={true}
            rfrr="PriceBreakDown.ViewHotel"
            linkName="Select property"
            data-stid="priceBreakDown-view-hotel"
            {...(options || {})}
          >
            {l10n.formatText('hotels.search.pricebreakdown.ctalabel')}
          </ButtonTracked>
        </DoubleLink>
      </UitkLink>
    </TargetableLink>
  );
  const childActions = extendedChildActions ? extendedChildActions : null;

  return <PriceBreakDown lodgingPriceBreakDown={lodgingPriceBreakDown} layerId={layerId} childActions={childActions} />;
};
PriceBreakDownWrapper.displayName = 'PriceBreakDownWrapper';

const PriceDescription = ({ text, theme }: PriceViewModel['priceMessage']) => {
  const themeValue = Theme[theme] || theme || ''; // TODO: Can remove once we switch to BEX API

  return (
    text && (
      <UitkText size={300} inline={true} dataTestId="content-hotel-sold-out" weight="medium" className={themeValue}>
        {text}
      </UitkText>
    )
  );
};
PriceDescription.displayName = 'PriceDescription';
